import React from 'react';
import { cn } from '../../../lib/utils';

// Define the skeleton component that won't re-render during loading
const TaskSkeletonComponent: React.FC = () => {
  // Define an array of column colors for the skeleton - default is 3 columns
  const columnColors = ['border-blue-300', 'border-yellow-300', 'border-green-300'];

  // Shimmer animation class for all elements
  const shimmerClass = 'animate-pulse';

  return (
    <div className="h-full w-full p-6 bg-[var(--content-bg-color)]">
      {/* Filter Header Skeleton - only right side elements */}
      <div className="flex items-center justify-end mb-4">
        <div className="flex items-center gap-2">
          <div className={cn('w-32 h-8 bg-gray-200 rounded-full', shimmerClass)} />
          <div className={cn('w-10 h-8 bg-gray-200 rounded-lg', shimmerClass)} />
        </div>
      </div>

      {/* Column Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 h-[calc(100%-3rem)] overflow-hidden">
        {/* Generate skeleton columns */}
        {columnColors.map((columnColor, columnIndex) => (
          <div key={columnIndex} className="flex flex-col h-full">
            {/* Column header */}
            <div className="flex items-center justify-between mb-3 p-2">
              <div className="flex items-center gap-2">
                <div className={cn('w-24 h-6 bg-gray-200 rounded', shimmerClass)} />
                <div className={cn('w-6 h-6 bg-gray-200 rounded-full', shimmerClass)} />
              </div>
              <div className="flex gap-1">
                <div className={cn('w-6 h-6 rounded-full bg-gray-200', shimmerClass)} />
                <div className={cn('w-6 h-6 rounded-full bg-gray-200', shimmerClass)} />
              </div>
            </div>

            {/* Task cards container - no background */}
            <div className="flex-1 overflow-hidden">
              <div className="space-y-3 h-full overflow-y-auto">
                {/* Fixed number of task cards per column */}
                {[...Array(columnIndex === 0 ? 3 : columnIndex === 1 ? 2 : 1)].map(
                  (_, cardIndex) => (
                    <div
                      key={cardIndex}
                      className={cn(
                        'p-4 bg-white rounded-md shadow-sm mb-3 border-l-[5px]',
                        columnColor
                      )}
                    >
                      {/* Task title */}
                      <div className={cn('h-5 bg-gray-200 rounded w-full mb-2', shimmerClass)} />

                      {/* Task description (simplified) */}
                      {cardIndex % 2 === 0 && (
                        <div className="mb-3 space-y-1">
                          <div className={cn('h-3 bg-gray-200 rounded w-full', shimmerClass)} />
                          <div className={cn('h-3 bg-gray-200 rounded w-5/6', shimmerClass)} />
                        </div>
                      )}

                      {/* Bottom row with deadline and assignee */}
                      <div className="flex items-center justify-between mt-2">
                        <div className={cn('h-4 bg-gray-200 rounded w-20', shimmerClass)} />

                        {/* Assignee avatar (sometimes) */}
                        {cardIndex % 3 !== 0 && (
                          <div className={cn('w-6 h-6 rounded-full bg-gray-200', shimmerClass)} />
                        )}
                      </div>

                      {/* Tags (sometimes) */}
                      {cardIndex % 2 === 1 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          <div className={cn('h-5 bg-gray-200 rounded-full w-16', shimmerClass)} />
                          <div className={cn('h-5 bg-gray-200 rounded-full w-12', shimmerClass)} />
                        </div>
                      )}
                    </div>
                  )
                )}

                {/* No add task button at bottom */}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Export a memoized version of the component to prevent unnecessary re-renders
export const TaskSkeleton = React.memo(TaskSkeletonComponent);
